import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react'
import { X } from 'lucide-react'

import {
  FILTER_CHIP_LABEL_CLASS,
  FILTER_CHIP_SHELL_PRESS_CLASS,
  FILTER_CHIP_VALUE_CLASS,
  FILTER_HOVER_BG_MUTED,
  FILTER_HOVER_BG_PRIMARY_20,
  FILTER_HOVER_BG_PRIMARY_25,
  FILTER_HOVER_TEXT_FOREGROUND,
  FILTER_HOVER_TEXT_PRIMARY,
} from '@/components/filters/filter-styles'
import { cn } from '@/lib/utils'

type LabelSegmentProps = {
  as: 'button' | 'span'
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
  React.HTMLAttributes<HTMLSpanElement>

function LabelSegment({ as, ...props }: LabelSegmentProps) {
  if (as === 'button') return <button type="button" {...props} />
  return <span {...props} />
}

export type SegmentedFilterChipTint = 'primary' | 'muted'

export type SegmentedFilterChipProps = {
  label: ReactNode
  value: ReactNode
  /** `primary` — applied filter (soft primary tint). `muted` — keyword/text chip. */
  tint?: SegmentedFilterChipTint
  /** Omit to render the label as a static segment (display-only chips). */
  onLabelClick?: () => void
  /** Omit to render the value as a static segment (display-only chips). */
  onValueClick?: () => void
  /** Omit to render without a remove segment (primary pill-bar dimensions). */
  onRemove?: () => void
  labelAriaLabel?: string
  valueAriaLabel?: string
  removeAriaLabel?: string
  labelTitle?: string
  /** Full value for tooltip when the rendered value is truncated. */
  valueTitle?: string
  /** Chips inside a focusable field (unified bar) must not steal its focus. */
  preventFocusSteal?: boolean
} & Omit<ComponentPropsWithoutRef<'span'>, 'onClick'>

/**
 * The single chip anatomy for every filter surface: [label] · [value] · [✕],
 * 26px soft-tint segments. Interaction stays with the caller — popover pills,
 * unified in-field chips, and standalone active-filter rows all render this.
 */
export const SegmentedFilterChip = forwardRef<HTMLSpanElement, SegmentedFilterChipProps>(
  function SegmentedFilterChip(
    {
      label,
      value,
      tint = 'primary',
      onLabelClick,
      onValueClick,
      onRemove,
      labelAriaLabel,
      valueAriaLabel,
      removeAriaLabel,
      labelTitle,
      valueTitle,
      preventFocusSteal = false,
      className,
      ...spanProps
    },
    ref,
  ) {
    const isMuted = tint === 'muted'
    const isInteractive = Boolean(onLabelClick || onValueClick || onRemove)
    const handleMouseDown = preventFocusSteal
      ? (event: React.MouseEvent) => event.preventDefault()
      : undefined
    const stopThenRun = (run: () => void) => (event: React.MouseEvent) => {
      if (preventFocusSteal) event.stopPropagation()
      run()
    }

    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex h-6.5 min-w-0 shrink-0 items-stretch overflow-hidden rounded-[var(--radius-button)] text-xs',
          isMuted
            ? 'max-w-[18rem] border border-border/50 bg-muted/60'
            : 'max-w-[16rem] border border-primary/30 bg-primary/15',
          isInteractive && FILTER_CHIP_SHELL_PRESS_CLASS,
          className,
        )}
        {...spanProps}
      >
        <LabelSegment
          as={onLabelClick ? 'button' : 'span'}
          onMouseDown={handleMouseDown}
          onClick={onLabelClick ? stopThenRun(onLabelClick) : undefined}
          className={cn(
            'flex min-w-0 max-w-[58%] shrink items-center overflow-hidden py-0.5 pl-2.5 pr-2 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            isMuted ? 'border-r border-border/40' : 'border-r border-primary/25',
            onLabelClick && (isMuted ? FILTER_HOVER_BG_MUTED : FILTER_HOVER_BG_PRIMARY_20),
          )}
          aria-label={labelAriaLabel}
          title={labelTitle}
        >
          <span className={cn(FILTER_CHIP_LABEL_CLASS, 'min-w-0 truncate text-foreground')}>
            {label}
          </span>
        </LabelSegment>
        <LabelSegment
          as={onValueClick ? 'button' : 'span'}
          onMouseDown={handleMouseDown}
          onClick={onValueClick ? stopThenRun(onValueClick) : undefined}
          className={cn(
            'flex min-w-0 flex-1 items-center overflow-hidden py-0.5 pl-2 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            onRemove ? 'pr-1' : 'pr-2.5',
            onValueClick && (isMuted ? FILTER_HOVER_BG_MUTED : FILTER_HOVER_BG_PRIMARY_20),
          )}
          aria-label={valueAriaLabel}
        >
          <span
            className={cn(
              FILTER_CHIP_VALUE_CLASS,
              'min-w-0 truncate',
              isMuted ? 'font-medium text-foreground' : 'font-semibold text-primary',
            )}
            title={valueTitle}
          >
            {value}
          </span>
        </LabelSegment>
        {onRemove ? (
          <button
            type="button"
            onMouseDown={handleMouseDown}
            onClick={stopThenRun(onRemove)}
            className={cn(
              'flex shrink-0 items-center px-1.5 text-muted-foreground transition-colors duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              isMuted
                ? cn(FILTER_HOVER_BG_MUTED, FILTER_HOVER_TEXT_FOREGROUND)
                : cn(FILTER_HOVER_BG_PRIMARY_25, FILTER_HOVER_TEXT_PRIMARY),
            )}
            aria-label={removeAriaLabel ?? 'Remove filter'}
          >
            <X className="h-3 w-3" aria-hidden />
          </button>
        ) : null}
      </span>
    )
  },
)
